import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Seo from "../../components/public/Seo";
import { createEditMeta } from "../../state/editsStore";
import { setActiveEditId } from "../../state/activeEditStore";
import { addToEdit } from "../../state/editStore";
import { apiFetch } from "../../lib/api";
import { optimizedImageUrl } from "../../lib/optimizedImageUrl";


function uid() {
  return `${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function resolveImageUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;

  // New CMS uploads live on backend
  if (url.startsWith("/tryon-assets/")) {
    return `${process.env.REACT_APP_API_BASE_URL || "http://localhost:8000"}${url}`;
  }

  // Legacy Veya edits live in frontend public/
  return url;
}

function seedEditFromVeyaEdit(v) {
  const meta = createEditMeta({ name: v.title, templateSlug: v.slug });
  const editId = meta.id;

  for (const imageUrl of v.images || []) {
    addToEdit(
      {
        id: uid(),
        createdAt: Date.now(),
        kind: "image",
        title: v.title,
        imageUrl: resolveImageUrl(imageUrl),
        sourceUrl: null,
      },
      editId
    );
  }

  for (const note of v.notes || []) {
    const text = String(note || "").trim();
    if (!text) continue;

    addToEdit(
      {
        id: uid(),
        createdAt: Date.now(),
        kind: "note",
        title: "Note",
        text,
        sourceUrl: null,
      },
      editId
    );
  }

  setActiveEditId(editId);
  return editId;
}

export default function MoodPage() {
  const { slug } = useParams();
  const [v, setV] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function load() {
      try {
        const data = await apiFetch(`/veya-team/edits/public/${encodeURIComponent(slug)}`);
setV(data);

      } catch (err) {
        console.error("Failed to load Veya edit", err);
        setError(err?.message || "Edit not found.");
      } finally {
        setLoading(false);
      }
    }

    load();
  }, [slug]);

  if (loading) {
    return <p className="public-lede">Loading edit...</p>;
  }

  if (error || !v) {
    return (
      <>
        <Seo title="Edit not found — Veya" />
        <p className="public-lede">Edit not found.</p>
        <Link className="public-navlink" to="/moods">
          Back to Veya’s Edits
        </Link>
      </>
    );
  }

  return (
    <>
      <Seo title={`${v.title} — Veya`} description={v.subtitle} />

      <div className="public-breadcrumb">
        <Link to="/moods" className="public-navlink">
          Veya’s Edits
        </Link>
        <span className="public-muted"> / {v.title}</span>
      </div>

      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <button
          className="public-cta"
          onClick={() => {
            const id = seedEditFromVeyaEdit(v);
            window.location.href = `/fitting-room?openedit=1&editId=${encodeURIComponent(id)}`;
          }}
        >
          Save & edit
        </button>

        <Link className="public-navlink" to="/moods">
          Back to Veya’s Edits
        </Link>
      </div>

      <div className="public-card" style={{ padding: 12, marginBottom: 12 }}>
        <div style={{ fontWeight: 900, fontSize: 18, lineHeight: 1.2 }}>{v.title}</div>
        <div className="public-muted" style={{ marginTop: 6 }}>
          {v.subtitle}
        </div>

        <div className="public-muted" style={{ marginTop: 10, lineHeight: 1.35 }}>
          This is a starting point. Saving creates your own copy — you can add/remove images, paste
          screenshots, and rewrite notes.
        </div>

        {v.notes?.length ? (
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
            {v.notes.map((t, idx) => (
              <span
                key={`${v.slug}_note_${idx}`}
                className="public-muted"
                style={{
                  fontSize: 11,
                  padding: "4px 10px",
                  borderRadius: 999,
                  border: "1px solid rgba(0,0,0,0.10)",
                  background: "rgba(0,0,0,0.03)",
                }}
              >
                {t}
              </span>
            ))}
          </div>
        ) : null}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
          gap: 14,
        }}
      >
        {(v.images || []).map((url) => (
          <div key={`${v.slug}_${url}`} className="public-card" style={{ overflow: "hidden", padding: 0 }}>
            <div style={{ aspectRatio: "4 / 5", background: "rgba(0,0,0,0.06)" }}>
              <img
  src={optimizedImageUrl(resolveImageUrl(url), { width: 700, quality: 80 })}
  alt=""
  loading="lazy"
  decoding="async"
  style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
/>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}